import { push } from 'redux-first-history';
import { getFormValues } from 'redux-form';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { FormName } from '@topconagriculture/feed-types';

import api from '../../../core/api';
import {
  notificationError,
  notificationSuccess,
  notificationWarning,
} from '../../../core/helpers/utils/notificationUtils';
import { compose403ErrorMessage } from '../../../core/helpers/utils/sagaUtils';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { managePaths } from '../../../routes/Manage/managePaths';
import { Forms } from '../../form/form.selector';
import { getSelectedMixer } from '../../selected/selected.selector';
import { LoadersNames } from '../../ui/ui.constants';
import { getFarmsListRequest } from '../farms/farms.actions';
import * as actions from './mixers.actions';
import { Mixer } from './mixers.types';

export function* getMixersListSaga() {
  yield put(showLoader(LoadersNames.fetchMixerList));
  try {
    const result = yield call(api.mixer.getMixerList);

    yield put(
      actions.getMixersList.done({
        params: {},
        result: result.map((elem) => ({
          ...elem,
          zoneIdList: elem.zoneIdList || [],
        })),
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchMixerList));
}

export function* getMixerDetailByIdSaga() {
  yield put(showLoader(LoadersNames.fetchMixer));
  try {
    const selectedMixer: Mixer = yield select(getSelectedMixer);

    const result = yield call(api.mixer.getMixerById, selectedMixer.id);

    yield put(
      actions.getMixerDetailById.done({
        params: {},
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchMixer));
}

export function* createMixerSaga(action: ReturnType<typeof actions.createMixer.started>) {
  yield put(showLoader(LoadersNames.createMixer));
  const {
    description,
    displayName,
    scaleTypeId,
    scaleNumber,
    statusCode,
    loadSize,
    service1,
    service2,
    useBluetooth,
    useDatalink,
    useUSB,
    useCL55,
    useFeedingSystem,
    mixerRoleCode,
    serialNumber,
  } = yield select(getFormValues(Forms.mixerCreationForm));
  const normalizedMixer = {
    description,
    displayName,
    scaleTypeId,
    scaleNumber,
    loadSize,
    statusCode,
    service1: { active: service1 },
    service2: { active: service2 },
    useBluetooth,
    useDatalink,
    useUSB,
    useCL55,
    useFeedingSystem,
    mixerRoleCode,
    serialNumber,
  };
  const formName = action.payload;
  try {
    const result = yield call(api.mixer.createMixer, normalizedMixer as any, formName);
    yield put(
      actions.createMixer.done({
        params: action.payload,
        result,
      }),
    );
    yield put(actions.getMixersList.started({}));
    yield put(notificationSuccess('mixers:add_mixer_success'));
    yield put(push(managePaths.mixers));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.createMixer));
}

export function* editMixerSaga(action: ReturnType<typeof actions.editMixer.started>) {
  yield put(showLoader(LoadersNames.editMixer));
  const {
    id,
    description,
    displayName,
    scaleTypeId,
    scaleNumber,
    statusCode,
    loadSize,
    service1,
    service2,
    useBluetooth,
    useDatalink,
    useUSB,
    useCL55,
    useFeedingSystem,
    mixerRoleCode,
    serialNumber,
  } = yield select(getFormValues(Forms.mixerCreationForm));
  const { isRedirecting, formName } = action.payload;
  const selectedMixer = yield select(getSelectedMixer);
  const normalizedMixer = {
    id: selectedMixer?.id ? selectedMixer.id : id,
    description,
    displayName,
    scaleTypeId,
    scaleNumber,
    loadSize,
    statusCode,
    service1: { active: service1.active },
    service2: { active: service2.active },
    useBluetooth,
    useDatalink,
    useUSB,
    useCL55,
    useFeedingSystem,
    mixerRoleCode,
    serialNumber,
  };
  try {
    const result = yield call(api.mixer.editMixer, normalizedMixer as any, formName);
    yield put(
      actions.editMixer.done({
        params: action.payload,
        result,
      }),
    );
    yield put(actions.getMixersList.started({}));
    yield put(notificationSuccess('mixers:edit_mixer_success'));
    if (isRedirecting) {
      yield put(push(managePaths.mixers));
    }
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.editMixer));
}

export function* deleteMixerSaga(action: ReturnType<typeof actions.deleteMixer.started>) {
  try {
    const selectedMixer: Mixer = yield select(getSelectedMixer);
    const formName = action.payload;

    yield call(api.mixer.deleteMixer, selectedMixer.id, formName);

    yield put(
      actions.deleteMixer.done({
        params: action.payload,
        result: selectedMixer.id,
      }),
    );
    yield put(actions.getMixersList.started({}));
    yield put(notificationSuccess('mixers:delete_mixer_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* getGateway() {
  yield put(showLoader(LoadersNames.downloadGateway));

  try {
    yield call(api.gateway.getGateway, 'application/octet-stream', 'zip');

    yield put(
      actions.downloadGateway.done({
        params: {},
        result: {},
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.downloadGateway));
}

export function* callMixerStatusSaga(action: ReturnType<typeof actions.callMixerStatus.started>) {
  const { id, formName } = action.payload;
  yield put(showLoader(LoadersNames.getMixerStatus));
  try {
    yield call(api.mixer.callMixerStatus, id, formName);
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
    yield put(hideLoader(LoadersNames.getMixerStatus));
  }
}

export function* getMixerStatusSaga(action: ReturnType<typeof actions.getMixerStatus.started>) {
  const mixerId = action.payload;
  try {
    const result = yield call(api.mixer.getMixerStatus, mixerId, FormName.mixerList);
    if (!result.state) {
      yield put(notificationWarning('mixers:mixer_is_busy_warning'));
    }
    yield put(
      actions.getMixerStatus.done({
        params: mixerId,
        result,
      }),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.getMixerStatus));
}

export function* watchMixersSaga() {
  yield all([
    takeLatest(actions.getMixersList.started, getMixersListSaga),
    takeLatest(getFarmsListRequest.done, getMixersListSaga),
    takeLatest(actions.getMixerDetailById.started, getMixerDetailByIdSaga),
    takeLatest(actions.createMixer.started, createMixerSaga),
    takeLatest(actions.editMixer.started, editMixerSaga),
    takeLatest(actions.deleteMixer.started, deleteMixerSaga),
    takeLatest(actions.downloadGateway.started, getGateway),
    takeLatest(actions.callMixerStatus.started, callMixerStatusSaga),
    takeLatest(actions.getMixerStatus.started, getMixerStatusSaga),
  ]);
}
